import dayjs from "dayjs";
import { useNetworkStore } from "@/composables/network/useNetwork";
import { useProjectsStore } from "@/store/modules/projects";
import {
  showErrorNotification,
  showSuccessNotification,
  showWarningNotification,
} from "@/composables/useNotifications";
import {
  PRELOAD_GROUPS,
  preloadedAt,
} from "@/composables/network/preloadFreshness";
import { i18nGlobal as i18n } from "@/setup/i18n";

const KEPT_NOTICE_TIMEOUT_MS = 8000;
export const KEPT_NOTICE_KEY = "kept-on-device";

let keptNoticeShown = false;

export const isOfflineMode = () => useNetworkStore().isOfflineMode;

export const isNetworkUnavailable = () =>
  !globalThis.navigator.onLine || isOfflineMode();

export const canEnterOfflineMode = () =>
  !!useProjectsStore().currentProject?.measurementPlanValidationDate;

export const lastServerReachLabel = () => {
  const { lastServerReachAt } = useNetworkStore();
  return lastServerReachAt
    ? i18n.t("lastServerReach", {
        date: dayjs(lastServerReachAt).format("DD/MM/YYYY"),
        time: dayjs(lastServerReachAt).format("HH:mm"),
      })
    : i18n.t("lastServerReachUnknown");
};

export const OFFLINE_FALLBACK_ROUTE = "project-recordbooks";

const ROUTES_OUTSIDE_MISSION = new Set([
  "not-found",
  "oidcCallback",
  "unauthorized-access-offline-mode",
]);

const isOnlineOnly = (route) =>
  route.matched.some(({ meta }) => meta.onlineOnly);

export const offlineMissionRedirect = (to) => {
  const { isOfflineMode: inOfflineMode, offlineMissionUuid } =
    useNetworkStore();
  if (!inOfflineMode || ROUTES_OUTSIDE_MISSION.has(to.name)) return null;
  const missionUuid = offlineMissionUuid || to.params.projectId;
  if (!missionUuid) return null;
  if (to.params.projectId === missionUuid && !isOnlineOnly(to)) return null;
  return {
    name: OFFLINE_FALLBACK_ROUTE,
    params: { projectId: missionUuid },
  };
};

export const leavePageUnavailableOffline = async () => {
  const { default: router } = await import("@/router");
  const redirect = offlineMissionRedirect(router.currentRoute.value);
  if (redirect) await router.push(redirect);
};

export const requestOfflineMode = async () => {
  const projectUuid = useProjectsStore().currentProjectUuid;
  const loadedAt = await Promise.all(
    PRELOAD_GROUPS.map((group) => preloadedAt(group, projectUuid))
  );
  if (!loadedAt.every(Boolean)) {
    useNetworkStore().setPreloadOpen(true);
    return;
  }
  await enterOfflineMode(projectUuid);
  const oldest = dayjs(Math.min(...loadedAt));
  showSuccessNotification(
    i18n.t("offlineWithStoredData", {
      date: oldest.format("DD/MM/YYYY"),
      time: oldest.format("HH:mm"),
    })
  );
};

const requestPersistentStorage = () =>
  globalThis.navigator.storage?.persist?.().catch((error) => {
    console.error("Persistent storage not granted", error);
    return false;
  });

export const enterOfflineMode = async (missionUuid) => {
  useNetworkStore().setOfflineMode(true, missionUuid);
  await Promise.all([
    requestPersistentStorage(),
    leavePageUnavailableOffline(),
  ]);
};

export const leaveOfflineMode = async () => {
  useNetworkStore().setOfflineMode(false);
  const { syncAfterOfflineMode } =
    await import("@/composables/network/usePendingSync");
  await syncAfterOfflineMode();
};

const goOnlineActions = () =>
  isOfflineMode()
    ? [
        {
          label: i18n.t("goOnline"),
          variant: "text",
          handler: leaveOfflineMode,
        },
      ]
    : [];

export const offlineFailureMessage = () =>
  i18n.t(isOfflineMode() ? "actionNeedsOnlineMode" : "actionNeedsNetwork");

export const notifyOfflineFailure = () =>
  showErrorNotification(offlineFailureMessage(), {
    actions: goOnlineActions(),
  });

export const notifyKeptOnDevice = () => {
  if (isOfflineMode() || keptNoticeShown) return;
  keptNoticeShown = true;
  requestPersistentStorage();
  showWarningNotification(i18n.t("savedOnDevice"), {
    timeout: KEPT_NOTICE_TIMEOUT_MS,
    key: KEPT_NOTICE_KEY,
  });
};

export const resetKeptNotice = () => {
  const wasShown = keptNoticeShown;
  keptNoticeShown = false;
  return wasShown;
};
